import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  EditorContent,
  useEditor,
  ReactNodeViewRenderer,
  Editor,
} from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import FontFamily from "@tiptap/extension-font-family";
import { ResizableImageNodeView } from "./components/ResizableImageNodeView";
import { TextSelection } from "prosemirror-state";
import { Plugin } from "prosemirror-state";
import { DOMParser as ProseMirrorDOMParser, Slice } from "prosemirror-model";
import { Extension, CommandProps, getMarkRange } from "@tiptap/core";
import { visibleScrollbar } from "../../utils/functions/get-visible-scrollbar";
import { createPortal } from "react-dom";
import { Placeholder } from "@tiptap/extensions";

interface Attachment {
  id: string;
  name: string;
  size?: number;
  type?: string;
  data?: Blob;
}

interface RichTextEditorProps {
  message?: string;
  onMessageChange?: (message: string) => void;
  onEditorReady?: (editor: Editor) => void;
  onAttachmentsChange?: (handler: (attachments: Attachment[]) => void) => void;
  onAttachmentsUpdate?: (attachments: Attachment[]) => void;
  placeholder?: string;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    fontSize: {
      setFontSize: (fontSize: string) => ReturnType;
      unsetFontSize: () => ReturnType;
    };
    highlight: {
      setHighlight: (attributes?: { color: string }) => ReturnType;
      toggleHighlight: (attributes?: { color: string }) => ReturnType;
      unsetHighlight: () => ReturnType;
    };
    textIndent: {
      indent: () => ReturnType;
      outdent: () => ReturnType;
    };
  }
}

const TextIndent = Extension.create({
  name: "textIndent",

  addGlobalAttributes() {
    return [
      {
        types: ["paragraph", "heading", "bulletList", "orderedList", "image"],
        attributes: {
          indentLevel: {
            default: 0,
            parseHTML: (element) => {
              const level = element.getAttribute("data-indent-level");
              return level ? parseInt(level, 10) : 0;
            },
            renderHTML: (attributes) => {
              if (!attributes.indentLevel || attributes.indentLevel === 0) {
                return {};
              }
              return {
                "data-indent-level": attributes.indentLevel,
                style: `margin-left: ${attributes.indentLevel * 1.5}rem;`,
              };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      indent:
        () =>
        ({ chain, state }: CommandProps) => {
          const { selection } = state;
          const { $from } = selection;

          const currentNode = $from.node();
          const currentLevel = currentNode.attrs.indentLevel || 0;

          if (currentLevel >= 50) return false;

          return chain()
            .updateAttributes(currentNode.type.name, {
              indentLevel: currentLevel + 1,
            })
            .run();
        },
      outdent:
        () =>
        ({ chain, state }: CommandProps) => {
          const { selection } = state;
          const { $from } = selection;

          const currentNode = $from.node();
          const currentLevel = currentNode.attrs.indentLevel || 0;

          if (currentLevel <= 0) return false;

          return chain()
            .updateAttributes(currentNode.type.name, {
              indentLevel: currentLevel - 1,
            })
            .run();
        },
    };
  },
});

const FontSize = Extension.create({
  name: "fontSize",
  addOptions() {
    return {
      types: ["textStyle"],
    };
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element: HTMLElement) =>
              element.style.fontSize.replace(/['"]+/g, ""),
            renderHTML: (attributes: { fontSize?: string | null }) => {
              if (!attributes.fontSize) {
                return {};
              }
              return {
                style: `font-size: ${attributes.fontSize}`,
              };
            },
          },
        },
      },
    ];
  },
  addCommands() {
    return {
      setFontSize:
        (fontSize: string) =>
        ({ chain }: CommandProps) => {
          return chain().setMark("textStyle", { fontSize }).run();
        },
      unsetFontSize:
        () =>
        ({ chain }: CommandProps) => {
          return chain()
            .setMark("textStyle", { fontSize: null })
            .removeEmptyTextStyle()
            .run();
        },
    };
  },
});

const CustomLink = Link.extend({
  inclusive: false,
  addProseMirrorPlugins() {
    const plugins: Plugin[] = [];

    const preventLinkExtensionPlugin = new Plugin({
      appendTransaction: (transactions, oldState, newState) => {
        const { doc, tr } = newState;
        let modified = false;

        if (!transactions.some((transaction) => transaction.docChanged)) {
          return null;
        }
        doc.descendants((node, pos) => {
          if (node.isText) {
            const marks = node.marks.filter(
              (mark) => mark.type.name === "link"
            );
            if (marks.length > 0) {
              const text = node.text || "";
              let start = 0;
              let end = text.length;

              while (start < text.length && /\s/.test(text[start])) {
                start++;
              }
              while (end > start && /\s/.test(text[end - 1])) {
                end--;
              }

              if (start > 0 || end < text.length) {
                const linkMark = marks[0];
                const linkAttrs = linkMark.attrs;

                tr.removeMark(pos, pos + text.length, linkMark.type);
                if (start < end) {
                  tr.addMark(
                    pos + start,
                    pos + end,
                    linkMark.type.create(linkAttrs)
                  );
                }

                modified = true;
              }
            }
          }
        });

        return modified ? tr : null;
      },
    });

    return [...plugins, preventLinkExtensionPlugin];
  },
});

const CustomImage = Image.configure({
  HTMLAttributes: {
    class: "resizable-image",
  },
  allowBase64: true,
}).extend({
  addAttributes() {
    return {
      width: {
        default: "100%",
        renderHTML: (attributes: { width?: string | null }) => {
          if (!attributes.width) {
            return {};
          }
          return {
            width: attributes.width,
          };
        },
        parseHTML: (element: HTMLElement) => element.getAttribute("width"),
      },
      textAlign: {
        default: null,
        parseHTML: (element: HTMLElement) => element.style.textAlign || null,
        renderHTML: (attributes: { textAlign?: string | null }) => {
          if (!attributes.textAlign) {
            return {};
          }
          return {
            style: `text-align: ${attributes.textAlign}`,
          };
        },
      },
      indentLevel: {
        default: 0,
        parseHTML: (element: HTMLElement) => {
          const level = element.getAttribute("data-indent-level");
          return level ? parseInt(level, 10) : 0;
        },
        renderHTML: (attributes: { indentLevel?: number }) => {
          if (!attributes.indentLevel || attributes.indentLevel === 0) {
            return {};
          }
          return {
            "data-indent-level": attributes.indentLevel,
            style: `margin-left: ${attributes.indentLevel * 1.5}rem;`,
          };
        },
      },
    };
  },
  addNodeView() {
    return ReactNodeViewRenderer(ResizableImageNodeView);
  },
});

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  message,
  onMessageChange,
  onEditorReady,
  onAttachmentsChange,
  onAttachmentsUpdate,
  placeholder,
}) => {
  const [editorHTML, setEditorHTML] = useState(message || "");
  const [isEditingLink, setIsEditingLink] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkText, setLinkText] = useState("");
  const [isLinkBubbleForcedHidden, setIsLinkBubbleForcedHidden] =
    useState(false);
  const currentAttachmentsRef = useRef<Attachment[]>([]);
  const [linkUrlError, setLinkUrlError] = useState<string>("");
  const [editMenuPosition, setEditMenuPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [hasLists, setHasLists] = useState(false);
  const [linkBubble, setLinkBubble] = useState<{
    href: string;
    top?: number;
    bottom?: number;
    startLeft: number;
    endRight: number;
  } | null>(null);
  const [linkBubbleWidth, setLinkBubbleWidth] = useState<number | null>(null);

  const bubbleMenuRef = useRef<HTMLDivElement>(null);
  const editorContainerRef = useRef<HTMLDivElement>(null);
  const onAttachmentsChangeRef = useRef(onAttachmentsChange);

  const measureLinkBubble = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      setLinkBubbleWidth(node.getBoundingClientRect().width);
    }
  }, []);

  const getLinkBubbleHorizontal = (bubble: NonNullable<typeof linkBubble>) => {
    const EDGE_BUFFER = 8;
    if (linkBubbleWidth === null) {
      return { left: Math.max(EDGE_BUFFER, bubble.startLeft) };
    }
    const fitsRight =
      bubble.startLeft + linkBubbleWidth + EDGE_BUFFER <= window.innerWidth;
    return fitsRight
      ? { left: Math.max(EDGE_BUFFER, bubble.startLeft) }
      : { right: Math.max(EDGE_BUFFER, window.innerWidth - bubble.endRight) };
  };

  const useOnClickOutside = (
    ref: React.RefObject<HTMLDivElement>,
    handler: (event: MouseEvent | TouchEvent) => void
  ) => {
    useEffect(() => {
      const listener = (event: MouseEvent | TouchEvent) => {
        if (!ref.current || ref.current.contains(event.target as Node)) {
          return;
        }
        handler(event);
      };
      document.addEventListener("mousedown", listener);
      document.addEventListener("touchstart", listener);
      return () => {
        document.removeEventListener("mousedown", listener);
        document.removeEventListener("touchstart", listener);
      };
    }, [ref, handler]);
  };

  useOnClickOutside(bubbleMenuRef, (event) => {
    const target = event.target as Element | null;
    if (target?.closest?.("[data-link-bubble]")) {
      return;
    }
    setIsEditingLink(false);
  });

  const checkForLists = useCallback((editor: Editor) => {
    const { doc } = editor.state;
    let hasListNodes = false;

    doc.descendants((node) => {
      if (node.type.name === "bulletList" || node.type.name === "orderedList") {
        hasListNodes = true;
        return false;
      }
    });

    setHasLists(hasListNodes);
  }, []);

  const editor = useEditor({
    immediatelyRender: false,
    extensions: [
      StarterKit.configure({
        // Remove extensions that are now included by default
        link: false,
        underline: false,
      }),
      CustomLink.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: {
          class:
            "text-verba-blue dark:text-verba-blue-dk underline cursor-pointer",
        },
      }),
      CustomImage.configure({
        HTMLAttributes: {
          class: "resizable-image",
        },
        allowBase64: true,
      }),
      Underline.configure(),
      Placeholder.configure({
        placeholder: placeholder || "Start typing your email here...",
        emptyEditorClass: "is-editor-empty",
      }),
      TextAlign.configure({
        types: ["heading", "paragraph", "image"],
      }),
      TextStyle.configure(),
      FontFamily.configure(),
      Color.configure(),
      FontSize.configure(),
      TextIndent.configure(),
    ],
    editorProps: {
      attributes: {
        class: "flex-grow focus:outline-none break-words",
      },
      handleKeyDown: (view, event) => {
        if (event.key === "Backspace" && editor?.isActive("link")) {
          setIsLinkBubbleForcedHidden(true);
        }
        return false;
      },
      handlePaste: (_view, event) => {
        if (!editor) {
          return false;
        }

        const files = event.clipboardData?.files;
        if (files && files.length > 0) {
          const imageFiles = Array.from(files).filter((file) =>
            file.type.startsWith("image/")
          );

          if (imageFiles.length > 0) {
            event.preventDefault();
            imageFiles.forEach((file) => {
              const reader = new FileReader();
              reader.onload = (e) => {
                if (!e.target?.result) {
                  return;
                }
                const dataUrl = e.target.result as string;
                const img = new window.Image();
                img.src = dataUrl;
                img.onload = () => {
                  const { clientWidth } = editor.view.dom;
                  const { naturalWidth } = img;
                  const width =
                    naturalWidth > clientWidth ? "100%" : `${naturalWidth}px`;

                  editor
                    .chain()
                    .insertContent({
                      type: "image",
                      attrs: { src: dataUrl, width },
                    })
                    .command(({ tr }) => {
                      const pos = tr.selection.to;
                      tr.setSelection(TextSelection.create(tr.doc, pos));
                      return true;
                    })
                    .focus()
                    .run();
                };
              };
              reader.readAsDataURL(file);
            });
            return true;
          }
        }

        const html = event.clipboardData?.getData("text/html");
        const text = event.clipboardData?.getData("text/plain");

        const URL_PATTERN = /(https?:\/\/\S+|www\.\S+)/g;

        const normalizeHref = (url: string) =>
          /^[a-zA-Z]+:\/\//.test(url) ? url : "https://" + url;

        const lineToContent = (line: string) =>
          line
            .split(URL_PATTERN)
            .filter((part) => part.length > 0)
            .map((part) =>
              /^(https?:\/\/\S+|www\.\S+)$/.test(part)
                ? {
                    type: "text",
                    text: part,
                    marks: [
                      { type: "link", attrs: { href: normalizeHref(part) } },
                    ],
                  }
                : { type: "text", text: part }
            );

        const textToParagraphs = (plainText: string) => {
          const lines = plainText.split(/\r?\n/);
          return lines.map((line) => ({
            type: "paragraph",
            content: line ? lineToContent(line) : [],
          }));
        };

        const hasMeaningfulHtml = (container: HTMLElement) => {
          const formattingSelector =
            "a, b, strong, i, em, u, s, strike, del, mark, sub, sup, code, pre, blockquote, ul, ol, li, h1, h2, h3, h4, h5, h6, table, img, font, [style]";
          return container.querySelector(formattingSelector) !== null;
        };

        if (html) {
          const probeDiv = document.createElement("div");
          probeDiv.innerHTML = html;

          if (!hasMeaningfulHtml(probeDiv)) {
            if (text) {
              event.preventDefault();
              const lines = text.split(/\r?\n/);
              // Single-line pastes stay inline at the cursor; wrapping in a
              // paragraph node would push the content onto a new line
              const content =
                lines.length === 1
                  ? lineToContent(text)
                  : textToParagraphs(text);
              editor.chain().focus().insertContent(content).run();
              return true;
            }
            return false;
          }

          event.preventDefault();

          const tempDiv = document.createElement("div");
          tempDiv.innerHTML = html;

          const dangerousTags = [
            "script",
            "style",
            "iframe",
            "object",
            "embed",
            "form",
            "input",
            "button",
            "select",
            "textarea",
          ];
          const dangerousAttrs = [
            "onclick",
            "onload",
            "onerror",
            "onmouseover",
            "onfocus",
            "onblur",
            "onchange",
            "onsubmit",
          ];

          dangerousTags.forEach((tag) => {
            const elements = tempDiv.querySelectorAll(tag);
            elements.forEach((el) => el.remove());
          });

          const allElements = tempDiv.querySelectorAll("*");
          allElements.forEach((el) => {
            dangerousAttrs.forEach((attr) => {
              el.removeAttribute(attr);
            });
          });

          if (tempDiv.innerHTML.trim()) {
            // Parse the sanitized DOM into a slice and replace the selection,
            // mirroring native paste. Avoids TipTap's insertContent raw-string
            // fallback (which shows literal tags for text-only HTML) and keeps
            // inline content inline instead of forcing a new paragraph.
            let slice = ProseMirrorDOMParser.fromSchema(
              editor.schema
            ).parseSlice(tempDiv, { preserveWhitespace: false });

            // Sources like code editors wrap a single line in <div> blocks,
            // which parses as a closed paragraph and pastes onto a new line.
            // Unwrap single-textblock slices so they insert inline at the
            // cursor.
            if (
              slice.content.childCount === 1 &&
              slice.content.firstChild?.isTextblock
            ) {
              slice = new Slice(slice.content.firstChild.content, 0, 0);
            }

            const { state: viewState } = editor.view;
            editor.view.dispatch(
              viewState.tr.replaceSelection(slice).scrollIntoView()
            );
            editor.view.focus();
          }
          return true;
        }

        if (text) {
          event.preventDefault();

          const trimmedText = text.trim();
          const { from, to } = editor.state.selection;
          const isSingleUrl = /^(https?:\/\/\S+|www\.\S+)$/.test(trimmedText);

          // Pasting a lone URL over selected text links the selection
          // instead of replacing it
          if (isSingleUrl && from !== to) {
            editor
              .chain()
              .focus()
              .setLink({ href: normalizeHref(trimmedText) })
              .run();
            return true;
          }

          const lines = text.split(/\r?\n/);
          // Single-line pastes stay inline at the cursor; wrapping in a
          // paragraph node would push the content onto a new line
          const content =
            lines.length === 1 ? lineToContent(text) : textToParagraphs(text);
          editor.chain().focus().insertContent(content).run();
          return true;
        }

        return false;
      },
    },
    autofocus: false,
    content: editorHTML,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      setEditorHTML(html);
      onMessageChange?.(html);
      checkForLists(editor);
    },
    onDestroy: () => {
      editor?.unregisterPlugin("linkBubbleMenu");
    },
    shouldRerenderOnTransaction: false,
  });

  useEffect(() => {
    if (editor && onEditorReady) {
      onEditorReady(editor);
    }
  }, [editor, onEditorReady]);

  useEffect(() => {
    if (editor && message !== undefined) {
      if (editor.getHTML() !== message) {
        editor.commands.setContent(message);
        setEditorHTML(message);
      }
    }
  }, [editor, message]);

  useEffect(() => {
    if (!editor) {
      return;
    }

    const updateLinkBubble = () => {
      if (isEditingLink || isLinkBubbleForcedHidden || !editor.isFocused) {
        setLinkBubble(null);
        return;
      }

      const { state } = editor;
      const { selection } = state;

      if (!selection.empty || !editor.isActive("link")) {
        setLinkBubble(null);
        return;
      }

      const $pos = selection.$from;
      const linkMark = $pos.marks().find((mark) => mark.type.name === "link");
      const href = linkMark?.attrs.href as string | undefined;

      if (!href || href.trim() === "") {
        setLinkBubble(null);
        return;
      }

      if ($pos.parentOffset === $pos.parent.nodeSize - 2) {
        setLinkBubble(null);
        return;
      }

      const linkRange = getMarkRange($pos, editor.schema.marks.link);
      const cursorCoords = editor.view.coordsAtPos(selection.from);
      const startCoords = linkRange
        ? editor.view.coordsAtPos(linkRange.from)
        : cursorCoords;
      const endCoords = linkRange
        ? editor.view.coordsAtPos(linkRange.to)
        : cursorCoords;

      const MIN_SPACE_ABOVE = 60;

      const vertical =
        cursorCoords.top >= MIN_SPACE_ABOVE
          ? { bottom: window.innerHeight - cursorCoords.top + 8 }
          : { top: cursorCoords.bottom + 8 };

      setLinkBubble({
        href,
        ...vertical,
        startLeft: startCoords.left,
        endRight: endCoords.right,
      });
    };

    const resetForcedHiddenOnBlur = () => {
      setIsLinkBubbleForcedHidden(false);
    };

    editor.on("selectionUpdate", updateLinkBubble);
    editor.on("transaction", updateLinkBubble);
    editor.on("focus", updateLinkBubble);
    editor.on("blur", updateLinkBubble);
    editor.on("blur", resetForcedHiddenOnBlur);

    return () => {
      editor.off("selectionUpdate", updateLinkBubble);
      editor.off("transaction", updateLinkBubble);
      editor.off("focus", updateLinkBubble);
      editor.off("blur", updateLinkBubble);
      editor.off("blur", resetForcedHiddenOnBlur);
    };
  }, [editor, isEditingLink, isLinkBubbleForcedHidden]);

  const handleRemoveLink = useCallback(() => {
    if (!editor) {
      return;
    }
    const { to } = editor.state.selection;
    editor
      .chain()
      .focus()
      .extendMarkRange("link")
      .unsetLink()
      .setTextSelection(to)
      .run();
    setIsEditingLink(false);
    setIsLinkBubbleForcedHidden(false);
  }, [editor]);

  const handleEditLink = useCallback(() => {
    if (!editor) {
      return;
    }

    editor.chain().focus().extendMarkRange("link").run();

    const { from, to } = editor.state.selection;
    const text = editor.state.doc.textBetween(from, to, " ");
    const linkAttrs = editor.getAttributes("link");

    setLinkText(text);
    setLinkUrl(linkAttrs.href);
    setLinkUrlError("");
    setIsEditingLink(true);
  }, [editor]);

  const validateUrl = useCallback(
    (url: string): { isValid: boolean; error?: string } => {
      const trimmedUrl = url.trim();

      if (!trimmedUrl) {
        return { isValid: false, error: "URL is required" };
      }

      try {
        const urlObj = new URL(trimmedUrl);

        const validProtocols = ["http:", "https:", "mailto:", "tel:", "ftp:"];
        if (!validProtocols.includes(urlObj.protocol)) {
          return {
            isValid: false,
            error:
              "Invalid protocol. Use http://, https://, mailto:, tel:, or ftp://",
          };
        }

        if (
          (urlObj.protocol === "http:" || urlObj.protocol === "https:") &&
          !urlObj.hostname
        ) {
          return { isValid: false, error: "Invalid URL format" };
        }

        return { isValid: true };
      } catch {
        let fixedUrl = trimmedUrl;

        if (!/^[a-zA-Z]+:\/\//.test(fixedUrl)) {
          fixedUrl = "https://" + fixedUrl;
        }

        try {
          new URL(fixedUrl);
          return { isValid: true };
        } catch {
          return { isValid: false, error: "Please enter a valid URL" };
        }
      }
    },
    []
  );

  const handleApplyLink = useCallback(() => {
    if (!editor) {
      return;
    }

    const url = linkUrl.trim();
    const text = linkText.trim();

    if (!url || !text) {
      handleRemoveLink();
      return;
    }
    const validation = validateUrl(url);

    if (!validation.isValid) {
      setLinkUrlError(validation.error || "Invalid URL");
      return;
    }
    setLinkUrlError("");

    let normalizedUrl = url;
    if (!/^[a-zA-Z]+:\/\//.test(normalizedUrl)) {
      normalizedUrl = "https://" + normalizedUrl;
    }
    const { from, to } = editor.state.selection;
    editor.chain().focus().unsetLink().run();
    editor
      .chain()
      .focus()
      .insertContentAt(
        { from, to },
        {
          type: "text",
          text: text,
          marks: [{ type: "link", attrs: { href: normalizedUrl } }],
        }
      )
      .setTextSelection(from + text.length)
      .run();

    setIsEditingLink(false);
  }, [editor, linkUrl, linkText, handleRemoveLink, validateUrl]);

  const handleAttachmentsChange = useCallback(
    (newAttachments: Attachment[]) => {
      const updatedAttachments = [
        ...currentAttachmentsRef.current,
        ...newAttachments,
      ];

      currentAttachmentsRef.current = updatedAttachments;
      onAttachmentsUpdate?.(updatedAttachments);
    },
    [onAttachmentsUpdate]
  );

  useEffect(() => {
    if (onAttachmentsChangeRef.current) {
      onAttachmentsChangeRef.current(handleAttachmentsChange);
    }
  }, [handleAttachmentsChange]);

  useEffect(() => {
    if (isEditingLink && editor && editorContainerRef.current) {
      const { from } = editor.state.selection;
      const coords = editor.view.coordsAtPos(from);
      const containerRect = editorContainerRef.current.getBoundingClientRect();

      const menuWidth = 320;
      const menuHeight = 180;
      const padding = 16;

      let top =
        coords.bottom -
        containerRect.top +
        editorContainerRef.current.scrollTop +
        8;

      let left =
        coords.left -
        containerRect.left +
        editorContainerRef.current.scrollLeft;

      let containerRight = window.innerWidth;
      let containerBottom = window.innerHeight;
      let element = editorContainerRef.current.parentElement;

      while (element) {
        const style = window.getComputedStyle(element);
        const hasOverflow =
          style.overflow === "hidden" ||
          style.overflow === "auto" ||
          style.overflowX === "hidden" ||
          style.overflowX === "auto" ||
          style.overflowY === "hidden" ||
          style.overflowY === "auto";

        if (hasOverflow) {
          const rect = element.getBoundingClientRect();
          containerRight = Math.min(containerRight, rect.right);
          containerBottom = Math.min(containerBottom, rect.bottom);
        }

        element = element.parentElement;
      }

      const availableRight = containerRight - (containerRect.left + left);
      if (availableRight < menuWidth + padding) {
        left = Math.max(
          0,
          containerRight - containerRect.left - menuWidth - padding
        );
      }

      const availableBottom = containerBottom - (containerRect.top + top);
      if (availableBottom < menuHeight + padding) {
        top = Math.max(
          0,
          coords.top -
            containerRect.top +
            editorContainerRef.current.scrollTop -
            menuHeight -
            8
        );
      }

      setEditMenuPosition({
        top,
        left,
      });
    }
  }, [isEditingLink, editor, editor?.state.selection]);

  const handleLinkUrlChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newUrl = e.target.value;
      setLinkUrl(newUrl);

      if (linkUrlError) {
        setLinkUrlError("");
      }
    },
    [linkUrlError]
  );

  if (!editor) {
    return <div>Loading editor...</div>;
  }

  return (
    <div
      ref={editorContainerRef}
      className="flex flex-col w-full h-full relative py-[12px]"
    >
      <div className="flex-1 overflow-y-auto flex flex-col min-h-0">
        {linkBubble &&
          !isEditingLink &&
          createPortal(
            <div
              key={linkBubble.href}
              ref={measureLinkBubble}
              data-link-bubble
              data-toolbar-portal
              onMouseDown={(e) => e.preventDefault()}
              style={{
                position: "fixed",
                ...(linkBubble.top !== undefined
                  ? { top: linkBubble.top }
                  : {}),
                ...(linkBubble.bottom !== undefined
                  ? { bottom: linkBubble.bottom }
                  : {}),
                ...getLinkBubbleHorizontal(linkBubble),
                zIndex: 9999999,
                pointerEvents: "auto",
              }}
              className="min-w-max max-w-[500px] flex items-center space-x-1 text-xs font-normal p-2 bg-fixed-white dark:bg-fixed-white-dk border border-border-gray-fixed dark:border-border-gray-fixed-dk rounded-[4px] shadow-lg"
            >
              <span className="text-verba-indigo-fixed dark:text-verba-indigo-fixed-dk shrink-0">
                Go to link:
              </span>
              <a
                href={linkBubble.href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-verba-blue dark:text-verba-blue-dk hover:underline max-w-[200px] shrink-0 truncate cursor-pointer"
                title={linkBubble.href}
                onMouseDown={(e) => {
                  e.preventDefault();
                  setIsLinkBubbleForcedHidden(true);
                  window.open(linkBubble.href, "_blank", "noopener,noreferrer");
                }}
              >
                {linkBubble.href}
              </a>
              <span className="text-verba-indigo-fixed dark:text-verba-indigo-fixed-dk">
                |
              </span>
              <button
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleEditLink();
                }}
                className="text-verba-blue dark:text-verba-blue-dk hover:underline"
              >
                Change
              </button>
              <span className="text-verba-indigo-fixed dark:text-verba-indigo-fixed-dk">
                |
              </span>
              <button
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleRemoveLink();
                }}
                className="text-verba-blue dark:text-verba-blue-dk hover:underline"
              >
                Remove
              </button>
            </div>,
            document.body
          )}

        {isEditingLink && editMenuPosition && (
          <div
            ref={bubbleMenuRef}
            data-toolbar-portal
            style={{
              position: "absolute",
              top: editMenuPosition.top,
              left: editMenuPosition.left,
              zIndex: 50,
            }}
            className="bg-fixed-white dark:bg-fixed-white-dk border border-border-gray-fixed dark:border-border-gray-fixed-dk rounded-[4px] shadow-popper-blur-effect p-4 min-w-[320px]"
          >
            <div className="space-y-2">
              <input
                type="text"
                value={linkText}
                onChange={(e) => setLinkText(e.target.value)}
                className="w-full px-2 py-1 rounded focus:outline-none text-sm text-verba-indigo-fixed dark:text-verba-indigo-fixed-dk bg-fixed-white dark:bg-fixed-white-dk border border-border-gray-fixed dark:border-border-gray-fixed-dk"
                placeholder="Text"
              />
              <div className="space-y-1">
                <input
                  type="text"
                  value={linkUrl}
                  onChange={handleLinkUrlChange}
                  className={`w-full px-2 py-1 rounded focus:outline-none text-sm text-verba-indigo-fixed dark:text-verba-indigo-fixed-dk bg-fixed-white dark:bg-fixed-white-dk border border-border-gray-fixed dark:border-border-gray-fixed-dk ${
                    linkUrlError
                      ? "border-verba-red-alert dark:border-verba-red-alert-dk"
                      : "border-border-gray-fixed dark:border-border-gray-fixed-dk"
                  }`}
                  placeholder="Type or paste a link"
                  autoFocus
                />
                {linkUrlError && (
                  <div className="text-xs text-verba-red-alert dark:text-verba-red-alert-dk font-medium">
                    {linkUrlError}
                  </div>
                )}
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => {
                    setIsEditingLink(false);
                    setLinkUrlError("");
                  }}
                  className="px-4 py-1 text-sm font-medium text-fixed-white dark:text-fixed-white-dk  bg-gray-75 dark:bg-gray-375 rounded"
                >
                  Cancel
                </button>
                <button
                  onClick={handleApplyLink}
                  disabled={!linkUrl || !linkText || !!linkUrlError}
                  className="px-4 py-1 text-sm font-medium text-fixed-white dark:text-fixed-white-dk bg-verba-blue dark:bg-verba-blue-dk rounded disabled:opacity-50"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        )}

        <div
          className={`flex-1 overflow-y-auto overflow-x-auto text-md text-verba-indigo-fixed dark:text-verba-indigo-fixed-dk flex flex-col rounded-[4px] pb-1.5 pr-2 cursor-text relative w-full min-h-0 ${visibleScrollbar(
            "[&::-webkit-scrollbar]:w-[6px]"
          )} ${visibleScrollbar("[&::-webkit-scrollbar]:h-[6px]")} ${
            hasLists ? "has-lists" : ""
          }`}
          onClick={(e) => {
            if (e.target === e.currentTarget && editor && !editor.isDestroyed) {
              editor
                .chain()
                .focus()
                .setTextSelection(editor.state.doc.content.size)
                .run();
            }
          }}
        >
          <EditorContent editor={editor} />
        </div>
      </div>
    </div>
  );
};

export default RichTextEditor;
